import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { marketingApi } from "../utils/api";

export function Visitors() {
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  const fetchVisitors = async () => {
    setLoading(true);
    try {
      const response = await marketingApi.getVisitors({
        page: currentPage,
        limit,
      });

      if (response && !response.error && response.data) {
        setVisitors(response.data.visitors || []);
        if (response.data.pagination) {
          setTotalPages(response.data.pagination.totalPages);
          setTotal(response.data.pagination.total);
        }
      } else {
        setVisitors([]);
        setTotalPages(0);
        setTotal(0);
      }
    } catch (error) {
      console.error("Error fetching visitors:", error);
      setVisitors([]);
      setTotalPages(0);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVisitors();
  }, [currentPage, limit]);

  const handleLimitChange = (value) => {
    setLimit(parseInt(value, 10));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <Button
          key="first"
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
        >
          <ChevronsLeft className="w-4 h-4" />
        </Button>
      );
    }

    pages.push(
      <Button
        key="prev"
        variant="outline"
        size="sm"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
    );

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={currentPage === i ? "default" : "outline"}
          size="sm"
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Button>
      );
    }

    pages.push(
      <Button
        key="next"
        variant="outline"
        size="sm"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    );

    if (endPage < totalPages) {
      pages.push(
        <Button
          key="last"
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          <ChevronsRight className="w-4 h-4" />
        </Button>
      );
    }

    return pages;
  };

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return "-";
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(d);
  };

  const formatLocation = (location) => {
    if (!location) return "-";
    const parts = [location.city, location.state, location.country].filter(Boolean);
    return parts.join(", ") || "-";
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="mb-2">Visitors</h1>
        <p className="text-muted-foreground">
          View all visitors and their tracking information
        </p>
      </div>

      {/* Controls */}
      <Card className="shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center justify-between">
            <div className="flex-1"></div> {/* Placeholder for search if added later */}
            
            {/* Limit selector */}
            <Select value={limit.toString()} onValueChange={handleLimitChange}>
              <SelectTrigger className="w-full sm:w-32">
                <SelectValue placeholder="Items per page" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Visitors Table */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>Visitor List ({total})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-muted-foreground">Loading visitors...</div>
            </div>
          ) : visitors.length === 0 ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-muted-foreground">No visitors found</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Visitor ID</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>First Seen</TableHead>
                    <TableHead>Latest Seen</TableHead>
                    <TableHead>First Source / Medium</TableHead>
                    <TableHead>Latest Source / Medium</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visitors.map((visitor) => (
                    <TableRow key={visitor.visitor_id} className="hover:bg-muted/50">
                      <TableCell className="font-medium text-xs">
                        {visitor.visitor_id}
                      </TableCell>
                      <TableCell>{formatLocation(visitor.location)}</TableCell>
                      <TableCell className="whitespace-nowrap">{formatDate(visitor.first_seen_at)}</TableCell>
                      <TableCell className="whitespace-nowrap">{formatDate(visitor.latest_seen_at)}</TableCell>
                      <TableCell>
                        {visitor.attribution?.utm_source || visitor.attribution?.utm_medium ? (
                          <div className="flex flex-col">
                            <span className="capitalize">{visitor.attribution.utm_source || "-"} / {visitor.attribution.utm_medium || "-"}</span>
                          </div>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        {visitor.attribution?.latest_utm_source || visitor.attribution?.latest_utm_medium ? (
                          <div className="flex flex-col">
                            <span className="capitalize">{visitor.attribution.latest_utm_source || "-"} / {visitor.attribution.latest_utm_medium || "-"}</span>
                          </div>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {!loading && totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-6">
              {renderPagination()}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
